import React, { useRef, useState } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import AccessibleDialog from "../../components/UI/Modals/AccessibleDialog/AccessibleDialog";
import { deleteAssociation } from "../../services/associationService";
import { AssociationsFetchResult } from "../../store/associationContext-actions";
import { AssociationResponse } from "../../types/association.types";
import {
  isAssociationNotAdministered,
  mapAssociationDeleteError,
  shouldRefetchAssociationsAfterError,
} from "../../utils/associationErrors";
import styles from "./AssociationProfilePage.module.scss";

// Association portal, slice 6: soft delete. Irreversible from the portal
// (undo is ADMIN provisioning), so the confirm button stays disabled until
// the association's name is typed back exactly. After a 200 the page re-reads
// `/association/mine`: the context selects the next administered association
// or the page falls to its empty state by itself.

export const DELETE_BUTTON_LABEL = "Obriši udruženje";
export const DELETE_CONFIRM_INPUT_LABEL =
  "Za potvrdu unesite tačan naziv udruženja";

const TITLE_ID = "association-delete-title";
const INPUT_ID = "association-delete-confirm-name";

/** The typed name must match the stored one; surrounding spaces do not count. */
export const nameMatches = (typed: string, name: string): boolean =>
  typed.trim() !== "" && typed.trim() === name.trim();

export interface DangerZoneCardProps {
  association: AssociationResponse;
  /** Any write on this page is in flight: delete waits for it. */
  busy: boolean;
  onPendingChange: (pending: boolean) => void;
  /** The page shows the copy and re-reads `/mine`. */
  onNotAdministered: () => void;
  /** The page shows the success notice (it outlives this card). */
  onDeleted: () => void;
  refetch: () => Promise<AssociationsFetchResult>;
}

const DangerZoneCard: React.FC<DangerZoneCardProps> = ({
  association,
  busy,
  onPendingChange,
  onNotAdministered,
  onDeleted,
  refetch,
}) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deletingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = nameMatches(typed, association.name);

  const openDialog = () => {
    if (busy) {
      return;
    }
    setTyped("");
    setError(null);
    setOpen(true);
  };

  const closeDialog = () => {
    if (deletingRef.current) {
      return;
    }
    setOpen(false);
  };

  const confirm = async () => {
    if (deletingRef.current || busy || !matches) {
      return;
    }
    deletingRef.current = true;
    setDeleting(true);
    onPendingChange(true);
    setError(null);
    try {
      await deleteAssociation(association.id);
      setOpen(false);
      onDeleted();
      await refetch();
    } catch (failure) {
      if (isAssociationNotAdministered(failure)) {
        setOpen(false);
        onNotAdministered();
        return;
      }
      setError(mapAssociationDeleteError(failure));
      if (shouldRefetchAssociationsAfterError(failure)) {
        void refetch();
      }
    } finally {
      deletingRef.current = false;
      setDeleting(false);
      onPendingChange(false);
    }
  };

  return (
    <section
      className={`${styles.card} ${styles.dangerCard}`}
      aria-labelledby="association-danger-title"
    >
      <h2 id="association-danger-title" className={styles.cardTitle}>
        Brisanje udruženja
      </h2>
      <p className={styles.hint}>
        Obrisano udruženje nestaje sa dobrocudan.rs i iz vaše liste. Savetnici
        i njihovi profili ostaju netaknuti. Vratiti ga može samo administrator.
      </p>
      <Button buttonType="red" disabled={busy} onClick={openDialog}>
        {DELETE_BUTTON_LABEL}
      </Button>

      <AccessibleDialog
        isOpen={open}
        onRequestClose={closeDialog}
        titleId={TITLE_ID}
        initialFocusRef={inputRef}
        maxWidth={480}
      >
        <div className={styles.dialog}>
          <h3 id={TITLE_ID} className={styles.dialogTitle}>
            Obrisati udruženje?
          </h3>
          <p className={styles.dialogCopy}>
            {`Udruženje „${association.name}“ biće obrisano i više ga nećete moći uređivati. Ovaj korak ne možete poništiti sami.`}
          </p>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor={INPUT_ID}>
              {DELETE_CONFIRM_INPUT_LABEL}
            </label>
            <input
              ref={inputRef}
              id={INPUT_ID}
              className={styles.confirmInput}
              type="text"
              autoComplete="off"
              value={typed}
              disabled={deleting}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          <div className={styles.dialogActions}>
            <Button buttonType="white" disabled={deleting} onClick={closeDialog}>
              Odustani
            </Button>
            <Button
              buttonType="red"
              disabled={!matches || deleting || busy}
              onClick={confirm}
            >
              {deleting ? "Brisanje..." : DELETE_BUTTON_LABEL}
            </Button>
          </div>
        </div>
      </AccessibleDialog>
    </section>
  );
};

export default DangerZoneCard;
